/**
 * PrimeEnergieGuide
 * ─────────────────────────────────────────────────────────────────
 * Guide éditorial complet pour /particuliers/aides/cee.
 *
 * Structure :
 *  1. Hero (eyebrow + titre + lead + sidecard + CTA)
 *  2. Bandeau "En résumé"
 *  3. Mécanisme du dispositif CEE + 3 acteurs
 *  4. Les familles de fiches résidentielles (BAR-TH, BAR-EN, BAR-EQ)
 *  5. Coups de pouce — bonifications
 *  6. Conditions d'éligibilité
 *  7. Démarches (5 étapes, avec règle d'or)
 *  8. Cumul avec les autres aides
 *  9. Pourquoi SEF (positionnement mandataire CEE)
 * 10. FAQ
 * 11. CTA final
 *
 * Contenu : `client/src/config/cee-content.ts` (source unique).
 */

import {
  ArrowRight,
  CheckCircle2,
  Info,
  ShieldAlert,
} from 'lucide-react';

import { Container } from '../layout/Container';
import { Button } from '../ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { ArticleLd } from '../seo/jsonld/Article';
import { BreadcrumbListLd } from '../seo/jsonld/BreadcrumbList';
import { FAQPageLd } from '../seo/jsonld/FAQPage';
import { HowToLd } from '../seo/jsonld/HowTo';
import { getIcon } from '../../lib/lucide-icons';
import { cn } from '../../lib/cn';
import {
  CTA_HREF,
  FeatureCallout,
  SectionHeader,
  SITE_URL,
} from './_shared';
import {
  PARTICULIER_URLS,
  ParticulierCTAFinal,
  ParticulierLiveCard,
  ParticulierPageHero,
} from '../audience/particulier';
import {
  CEE_ACTEURS,
  CEE_BONIFICATIONS,
  CEE_CONDITIONS,
  CEE_CUMULS,
  CEE_DEMARCHES,
  CEE_FAMILLES,
  CEE_FAQ,
  CEE_HERO,
  CEE_MECANISME,
  CEE_SEF_FEATURES,
  CEE_SUMMARY,
} from '../../config/cee-content';

const PAGE_URL = `${SITE_URL}/particuliers/aides/cee`;

const CUMUL_TONE_STYLES: Record<
  'brand' | 'accent' | 'sage' | 'lavender' | 'ivory',
  string
> = {
  brand: 'bg-brand-50 text-brand-700 ring-brand-200/60',
  accent: 'bg-accent-50 text-accent-800 ring-accent-200/60',
  sage: 'bg-stone-100 text-stone-700 ring-stone-200/60',
  lavender: 'bg-brand-50 text-brand-700 ring-brand-200/60',
  ivory: 'bg-ivory-100 text-stone-700 ring-stone-200/60',
};

// ════════════════════════════════════════════════════════════════
// Composant principal
// ════════════════════════════════════════════════════════════════

export function PrimeEnergieGuide() {
  return (
    <>
      <BreadcrumbListLd
        items={[
          { name: 'Accueil', url: `${SITE_URL}/` },
          { name: 'Particuliers', url: `${SITE_URL}/particuliers` },
          { name: 'Aides', url: `${SITE_URL}/particuliers/aides` },
          { name: 'Prime énergie (CEE)', url: PAGE_URL },
        ]}
      />
      <ArticleLd
        headline="Prime énergie (CEE) 2026 — Le guide complet du dispositif"
        url={PAGE_URL}
        datePublished="2026-01-01"
        dateModified={new Date().toISOString().slice(0, 10)}
      />
      <FAQPageLd
        items={CEE_FAQ.map((item) => ({
          question: item.question,
          answer: item.answer,
        }))}
      />
      <HowToLd
        name="Comment obtenir la prime énergie (CEE) en 2026"
        description="Les 5 étapes pour bénéficier de la prime énergie issue du dispositif des Certificats d'Économies d'Énergie."
        steps={CEE_DEMARCHES.map((d) => ({ name: d.title, text: d.description }))}
      />

      <HeroSection />
      <SummarySection />
      <MecanismeSection />
      <FamillesSection />
      <BonificationsSection />
      <ConditionsSection />
      <DemarchesSection />
      <CumulSection />
      <PourquoiSefSection />
      <FaqSection />
      <FinalCtaSection />
    </>
  );
}

// ════════════════════════════════════════════════════════════════
// 1. HERO
// ════════════════════════════════════════════════════════════════

function HeroSection() {
  const cumulables = ['MaPrimeRénov’', 'Éco-PTZ', 'TVA à 5,5 %', 'Coup de pouce', 'Aides locales'];
  return (
    <ParticulierPageHero
      breadcrumb={[
        { name: 'Aides', href: '/particuliers/aides' },
        { name: 'Prime énergie (CEE)' },
      ]}
      eyebrow={CEE_HERO.eyebrow}
      headline={CEE_HERO.titlePrimary}
      headlineAccent={CEE_HERO.titleAccent}
      subheadline={CEE_HERO.lead}
      primaryCta={{ label: 'Estimer ma prime énergie', href: CTA_HREF }}
      secondaryCta={{ label: 'Voir le catalogue des fiches CEE', href: '/cee/fiches?secteur=BAR' }}
      microPromise={CEE_HERO.microPromise}
      sideSlot={
        <ParticulierLiveCard eyebrow="Particularité du dispositif">
          <p className="bg-gradient-to-br from-accent-700 via-accent-600 to-brand-600 bg-clip-text text-2xl font-black leading-tight tracking-tight text-transparent">
            {CEE_HERO.amountHeadline}
          </p>
          <p className="mt-3 text-[13px] leading-relaxed text-stone-500">
            {CEE_HERO.amountSubline}
          </p>
          <div className="mt-5 border-t border-stone-100 pt-5">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-400">
              Cumulable avec
            </p>
            <ul className="mt-3 space-y-1.5 text-[13px] text-stone-700">
              {cumulables.map((label) => (
                <li key={label} className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-3 w-3 shrink-0 text-brand-500" strokeWidth={2.4} />
                  {label}
                </li>
              ))}
            </ul>
          </div>
        </ParticulierLiveCard>
      }
    />
  );
}

// ════════════════════════════════════════════════════════════════
// 2. EN RÉSUMÉ
// ════════════════════════════════════════════════════════════════

function SummarySection() {
  return (
    <section className="border-y border-stone-200/70 bg-stone-50/60 py-10 md:py-14">
      <Container>
        <div className="rounded-2xl border border-stone-200 bg-paper p-6 shadow-subtle md:p-8">
          <div className="mb-5 flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-accent-100 text-accent-800">
              <CheckCircle2 className="h-4 w-4" strokeWidth={2} />
            </span>
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-stone-500">
              En résumé
            </p>
          </div>

          <ul className="grid gap-3 md:grid-cols-2">
            {CEE_SUMMARY.map((point) => (
              <li
                key={point.text}
                className="flex items-start gap-2.5 rounded-xl border border-stone-100 bg-stone-50/50 p-3.5"
              >
                <CheckCircle2
                  className="mt-0.5 h-4 w-4 shrink-0 text-brand-500"
                  strokeWidth={2.4}
                />
                <span className="text-[13.5px] leading-relaxed text-stone-700">
                  {point.text}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 3. MÉCANISME + ACTEURS
// ════════════════════════════════════════════════════════════════

function MecanismeSection() {
  return (
    <section className="py-14 md:py-20">
      <Container>
        <SectionHeader
          eyebrow={CEE_MECANISME.eyebrow}
          title={CEE_MECANISME.title}
          subtitle={CEE_MECANISME.description}
        />

        <div className="grid gap-5 md:grid-cols-3">
          {CEE_ACTEURS.map((acteur) => {
            const Icon = getIcon(acteur.iconName);
            return (
              <article
                key={acteur.title}
                className="flex flex-col gap-3 rounded-2xl border border-stone-200 bg-paper p-6 shadow-subtle"
              >
                <div className="flex items-center justify-between">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-200/60">
                    <Icon className="h-5 w-5" strokeWidth={1.7} />
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-stone-400">
                    {acteur.role}
                  </span>
                </div>
                <h3 className="font-display text-lg font-bold text-ink">
                  {acteur.title}
                </h3>
                <p className="text-[13.5px] leading-relaxed text-stone-600">
                  {acteur.description}
                </p>
              </article>
            );
          })}
        </div>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 4. FAMILLES DE FICHES
// ════════════════════════════════════════════════════════════════

function FamillesSection() {
  return (
    <section className="border-t border-stone-100 bg-stone-50/40 py-14 md:py-20">
      <Container>
        <SectionHeader
          eyebrow="Catalogue résidentiel"
          title="Les 3 grandes familles de fiches CEE résidentielles"
          subtitle="Chaque opération éligible est encadrée par une fiche d’opération standardisée publiée par arrêté. Les fiches résidentielles commencent par le préfixe BAR-*."
        />

        <div className="grid gap-5 md:grid-cols-3">
          {CEE_FAMILLES.map((famille) => {
            const Icon = getIcon(famille.iconName);
            return (
              <article
                key={famille.code}
                className="flex flex-col gap-4 rounded-2xl border border-stone-200 bg-paper p-6 shadow-subtle"
              >
                <div className="flex items-center justify-between">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-200/60">
                    <Icon className="h-5 w-5" strokeWidth={1.7} />
                  </span>
                  <span className="rounded-md bg-stone-900/90 px-2 py-1 font-mono text-[10.5px] font-semibold text-ivory-50">
                    {famille.code}-*
                  </span>
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold text-ink">
                    {famille.label}
                  </h3>
                  <p className="mt-2 text-[13px] leading-relaxed text-stone-600">
                    {famille.description}
                  </p>
                </div>
                <ul className="mt-1 space-y-1.5 border-t border-stone-100 pt-3">
                  {famille.examples.map((ex) => (
                    <li
                      key={ex}
                      className="flex items-start gap-2 text-[12.5px] leading-relaxed text-stone-700"
                    >
                      <CheckCircle2
                        className="mt-1 h-3.5 w-3.5 shrink-0 text-brand-500"
                        strokeWidth={2.2}
                      />
                      <span>{ex}</span>
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Button asChild variant="outline" size="md">
            <a href="/cee/fiches?secteur=BAR">
              Explorer toutes les fiches CEE résidentielles
              <ArrowRight className="h-4 w-4" />
            </a>
          </Button>
          <span className="text-[12.5px] text-stone-500">
            Plus de 100 fiches d’opérations standardisées dans le résidentiel.
          </span>
        </div>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 5. COUPS DE POUCE (BONIFICATIONS)
// ════════════════════════════════════════════════════════════════

function BonificationsSection() {
  return (
    <section className="py-14 md:py-20">
      <Container>
        <SectionHeader
          eyebrow="Bonifications CEE"
          title="Les Coups de pouce : la prime énergie majorée"
          subtitle="Sur certaines opérations prioritaires, l’État met en place des bonifications appelées « Coups de pouce ». La prime de base est majorée, parfois doublée, pour les ménages aux revenus modestes."
        />

        <div className="grid gap-4 md:grid-cols-3">
          {CEE_BONIFICATIONS.map((b) => {
            const Icon = getIcon(b.iconName);
            return (
              <article
                key={b.title}
                className="flex flex-col gap-3 rounded-2xl border border-stone-200 bg-paper p-5 shadow-subtle"
              >
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-accent-50 text-accent-800 ring-1 ring-inset ring-accent-200/60">
                  <Icon className="h-4 w-4" strokeWidth={1.7} />
                </span>
                <div>
                  <h3 className="font-display text-base font-bold text-ink">
                    {b.title}
                  </h3>
                  <p className="mt-1 text-[11.5px] font-semibold uppercase tracking-[0.08em] text-stone-500">
                    {b.target}
                  </p>
                </div>
                <p className="text-[12.5px] leading-relaxed text-stone-600">
                  {b.description}
                </p>
              </article>
            );
          })}
        </div>

        <p className="mt-6 flex items-start gap-2.5 rounded-xl bg-stone-100/60 p-4 text-[12.5px] leading-relaxed text-stone-600 ring-1 ring-inset ring-stone-200">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-stone-500" strokeWidth={1.8} />
          La liste des opérations bénéficiant d’un Coup de pouce évolue régulièrement par
          arrêté. Pour le détail des bonifications en vigueur en 2026, consultez la page
          dédiée{' '}
          <a
            href="/particuliers/aides/coup-de-pouce"
            className="ml-1 font-semibold text-brand-700 hover:underline"
          >
            Coup de pouce
          </a>
          .
        </p>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 6. CONDITIONS
// ════════════════════════════════════════════════════════════════

function ConditionsSection() {
  return (
    <section className="border-t border-stone-100 bg-stone-50/40 py-14 md:py-20">
      <Container>
        <SectionHeader
          eyebrow="Conditions d’éligibilité"
          title="Les 6 conditions à respecter pour toucher la prime"
          subtitle="Le non-respect d’une seule de ces conditions entraîne le rejet du dossier. La règle la plus stricte est l’engagement AVANT signature du devis."
        />

        <ul className="space-y-3">
          {CEE_CONDITIONS.map((c) => (
            <li
              key={c}
              className="flex items-start gap-3 rounded-xl border border-stone-200 bg-paper p-4 shadow-subtle"
            >
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-500" strokeWidth={2.2} />
              <span className="text-[14px] leading-relaxed text-stone-700">{c}</span>
            </li>
          ))}
        </ul>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 7. DÉMARCHES — Timeline 5 étapes
// ════════════════════════════════════════════════════════════════

function DemarchesSection() {
  return (
    <section className="py-14 md:py-20">
      <Container>
        <SectionHeader
          eyebrow="Comment ça marche"
          title="5 étapes pour obtenir votre prime énergie"
          subtitle="L’ordre des étapes est crucial : engager la demande AVANT de signer le devis est non négociable. C’est la première cause de refus des dossiers CEE."
        />

        <ol className="grid gap-4 md:gap-3">
          {CEE_DEMARCHES.map((step, idx) => {
            const Icon = getIcon(step.iconName);
            const isLast = idx === CEE_DEMARCHES.length - 1;
            return (
              <li
                key={step.step}
                className="relative flex items-start gap-5 rounded-2xl border border-stone-200 bg-paper p-5 shadow-subtle md:p-6"
              >
                <div className="flex shrink-0 flex-col items-center gap-2">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-brand-600 to-accent-600 text-base font-bold text-white shadow-glow">
                    {step.step}
                  </span>
                  {!isLast ? (
                    <span aria-hidden className="hidden h-full w-px flex-1 bg-stone-200 md:block" />
                  ) : null}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-200/60">
                      <Icon className="h-4 w-4" strokeWidth={1.7} />
                    </span>
                    <h3 className="font-display text-base font-bold text-ink md:text-lg">
                      {step.title}
                    </h3>
                  </div>
                  <p className="mt-2 text-[13.5px] leading-relaxed text-stone-600">
                    {step.description}
                  </p>
                  {step.warning ? (
                    <p className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50/80 p-3 text-[12.5px] leading-relaxed text-amber-900 ring-1 ring-inset ring-amber-200/70">
                      <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={2} />
                      <span>{step.warning}</span>
                    </p>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ol>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 8. CUMUL
// ════════════════════════════════════════════════════════════════

function CumulSection() {
  return (
    <section className="border-t border-stone-100 bg-stone-50/40 py-14 md:py-20">
      <Container>
        <SectionHeader
          eyebrow="Cumul avec les autres aides"
          title="La prime énergie se cumule avec tous les autres dispositifs"
          subtitle="C’est l’un des avantages clés du dispositif CEE : pas d’exclusivité, pas de plafond propre. La prime énergie s’ajoute systématiquement aux aides publiques."
        />

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {CEE_CUMULS.map((c) => {
            const Icon = getIcon(c.iconName);
            const Tag = c.href ? 'a' : 'div';
            return (
              <Tag
                key={c.aide}
                {...(c.href ? { href: c.href } : {})}
                className={cn(
                  'group flex flex-col gap-3 rounded-2xl border border-stone-200 bg-paper p-5 shadow-subtle',
                  c.href && 'transition-all duration-200 hover:-translate-y-0.5 hover:shadow-medium',
                )}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={cn(
                      'inline-flex h-9 w-9 items-center justify-center rounded-lg ring-1 ring-inset',
                      CUMUL_TONE_STYLES[c.tone],
                    )}
                  >
                    <Icon className="h-4 w-4" strokeWidth={1.7} />
                  </span>
                  {c.href ? (
                    <ArrowRight
                      className="h-3.5 w-3.5 text-stone-400 transition-transform duration-150 group-hover:translate-x-0.5 group-hover:text-brand-600"
                      strokeWidth={2.4}
                    />
                  ) : null}
                </div>
                <p className="text-[14px] font-semibold text-ink">{c.aide}</p>
                <p className="text-[12.5px] leading-relaxed text-stone-600">{c.description}</p>
              </Tag>
            );
          })}
        </div>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 9. POURQUOI SEF
// ════════════════════════════════════════════════════════════════

function PourquoiSefSection() {
  return (
    <section className="py-14 md:py-20">
      <Container>
        <SectionHeader
          eyebrow="Notre rôle"
          title="Solution Éco France, mandataire CEE pour votre projet"
          subtitle="Acteur intégré de la rénovation énergétique, Solution Éco France monte et dépose les dossiers CEE pour son réseau d’artisans RGE partenaires. Nous pouvons aussi être mandataires pour des artisans tiers qui nous le demandent."
        />

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {CEE_SEF_FEATURES.map((f) => (
            <FeatureCallout
              key={f.title}
              iconName={f.iconName}
              title={f.title}
              text={f.text}
            />
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3 rounded-2xl border border-brand-200 bg-brand-50/50 p-5">
          <p className="text-[13.5px] leading-relaxed text-brand-900">
            <span className="font-bold">Comment ça se traduit pour vous :</span>{' '}
            avec notre réseau d’artisans RGE, la prime énergie est déduite directement
            de votre devis. Zéro avance de trésorerie sur le volet CEE, zéro paperasse
            à gérer en parallèle de vos travaux.
          </p>
          <Button asChild variant="primary" size="md">
            <a href={CTA_HREF}>
              Estimer ma prime
              <ArrowRight className="h-4 w-4" />
            </a>
          </Button>
        </div>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 10. FAQ
// ════════════════════════════════════════════════════════════════

function FaqSection() {
  return (
    <section className="border-t border-stone-100 bg-stone-50/40 py-14 md:py-20">
      <Container size="narrow">
        <SectionHeader
          eyebrow="FAQ"
          title="Vos questions sur la prime énergie (CEE)"
          subtitle="Les questions les plus fréquentes posées par les ménages en 2026, traitées avec un angle pratique."
          align="center"
        />

        <div className="overflow-hidden rounded-2xl border border-stone-200 bg-paper shadow-subtle">
          <Accordion type="single" collapsible className="divide-y divide-stone-100">
            {CEE_FAQ.map((item, idx) => (
              <AccordionItem
                key={item.question}
                value={`cee-faq-${idx}`}
                className="border-0 px-5"
              >
                <AccordionTrigger className="text-[15px]">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-[14px] leading-relaxed text-stone-600">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// 11. CTA FINAL
// ════════════════════════════════════════════════════════════════

function FinalCtaSection() {
  return (
    <ParticulierCTAFinal
      badge="Calcul personnalisé · 60 sec"
      title="Combien vaut votre prime énergie en 2026 ?"
      description={
        <>
          Décrivez votre projet : type de logement, zone climatique, travaux envisagés.
          Notre calculateur estime votre prime énergie au tarif du marché du jour et
          croise les autres aides cumulables (MaPrimeRénov&rsquo;, éco-PTZ, TVA 5,5 %).
        </>
      }
      primaryCta={{ label: 'Estimer ma prime', href: CTA_HREF }}
      secondaryCta={{ label: 'Parler à un conseiller', href: PARTICULIER_URLS.contact }}
      footnote="Aucune carte bancaire · aucune inscription · estimation au tarif du marché du jour."
    />
  );
}
